import {
  UzAlert,
  UzButton,
  UzCombobox,
  UzForm,
  UzFormField,
  UzInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import React, { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { FaqBuilder } from '../components/FaqBuilder';
import { MediaDropField } from '../components/MediaDropField';
import { RichTextEditor } from '../components/RichTextEditor';

import { blogPublicUrl, SITE_URL } from './blog-url';
import {
  useBlogCategories,
  useBlogDestinations,
  useBlogThemes,
  useBlogPost,
  useCreateBlogPost,
  useUpdateBlogPost,
} from './use-blog';

import { useSubmitForReview } from '@/features/website-approvals/use-website-approvals';
import { useListReturn } from '@/hooks/use-list-return';
import { WEBSITE_PUBLISH_STATUS_LABEL } from '@/lib/enum-labels';

/** Title → kebab-case slug (lowercase letters, digits, dashes). */
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Lenient normalisation while the author is typing a custom slug: lowercase and
 * turn invalid runs into a dash, but keep leading/trailing dashes so words can
 * be typed one after another. `slugify` does the final tidy-up on blur.
 */
const softSlug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9-]+/g, '-');

interface BlogFormValues {
  title: string;
  slug: string;
  excerpt: string;
  categoryId: string;
  destinationId: string;
  themeId: string;
  cityId: string;
  coverMediaId: string | null;
  bodyHtml: string;
  faqHtml: string;
  metaTitle: string;
  metaDescription: string;
  isFeatured: boolean;
}

const EMPTY: BlogFormValues = {
  title: '',
  slug: '',
  excerpt: '',
  categoryId: '',
  destinationId: '',
  themeId: '',
  cityId: '',
  coverMediaId: null,
  bodyHtml: '',
  faqHtml: '',
  metaTitle: '',
  metaDescription: '',
  isFeatured: false,
};

/** Build the create/update payload from the form values (shared by both). */
const payloadFrom = (values: BlogFormValues) => ({
  title: values.title.trim(),
  slug: values.slug.trim(),
  excerpt: values.excerpt.trim() || null,
  categoryId: values.categoryId || null,
  destinationId: values.destinationId || null,
  themeId: values.themeId || null,
  cityId: values.cityId || null,
  coverMediaId: values.coverMediaId || null,
  bodyHtml: values.bodyHtml || undefined,
  faqHtml: values.faqHtml || null,
  metaTitle: values.metaTitle.trim() || null,
  metaDescription: values.metaDescription.trim() || null,
  isFeatured: values.isFeatured,
});

/** Shopify-style card: title header + stacked fields (matches ActivityEditPage). */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

/**
 * Full-page blog create/edit form.
 *
 *   /blogs/new   mode=create
 *   /blogs/:id   mode=edit
 *
 * Single sticky save bar (Create / Save) drives the whole form - no per-section
 * saves - mirroring the Activities editor's layout and patterns.
 */
export const BlogFormPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToList = useListReturn('/blogs');
  const isCreate = !id || id === 'new';

  const detailQuery = useBlogPost(isCreate ? null : (id ?? null));
  const post = detailQuery.data ?? null;

  const categoriesQuery = useBlogCategories();
  const destinationsQuery = useBlogDestinations();
  const themesQuery = useBlogThemes();

  const create = useCreateBlogPost();
  const update = useUpdateBlogPost();
  const submitForReview = useSubmitForReview();
  const pending = create.isPending || update.isPending;

  const form = useForm<BlogFormValues>({ defaultValues: EMPTY });
  const slugEdited = useRef(false);
  // Tracks which record the form is currently hydrated from, so background
  // refetches (after a save, on window refocus, etc.) don't reset the form
  // out from under in-progress edits. We only re-seed when the target changes.
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (isCreate) {
      if (loadedFor.current === 'new') return;
      loadedFor.current = 'new';
      slugEdited.current = false;
      form.reset(EMPTY);
    } else if (post) {
      if (loadedFor.current === post.id) return;
      loadedFor.current = post.id;
      slugEdited.current = true;
      form.reset({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt ?? '',
        categoryId: post.categoryId ?? '',
        destinationId: post.destinationId ?? '',
        themeId: post.themeId ?? '',
        cityId: post.cityId ?? '',
        coverMediaId: post.coverMediaId ?? null,
        bodyHtml: post.bodyHtml ?? '',
        faqHtml: post.faqHtml ?? '',
        metaTitle: post.metaTitle ?? '',
        metaDescription: post.metaDescription ?? '',
        isFeatured: post.isFeatured ?? false,
      });
    }
  }, [isCreate, post, form]);

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'No category' },
      ...(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [categoriesQuery.data],
  );

  const destinationOptions = useMemo(
    () => [
      { value: '', label: 'No destination' },
      ...(destinationsQuery.data ?? []).map((d) => ({ value: d.id, label: d.label })),
    ],
    [destinationsQuery.data],
  );

  const themeOptions = useMemo(
    () => [
      { value: '', label: 'No theme' },
      ...(themesQuery.data ?? []).map((t) => ({ value: t.id, label: t.label })),
    ],
    [themesQuery.data],
  );

  const title = form.watch('title');
  const slug = form.watch('slug');
  const canSubmit = title.trim().length > 0 && slug.trim().length > 0 && !pending;

  // One action saves the whole form (create then go to edit, or update in place).
  const submit = form.handleSubmit(async (values) => {
    const payload = payloadFrom(values);
    if (isCreate) {
      const created = await create.mutateAsync(payload);
      navigate(`/blogs/${created.id}`);
    } else if (post) {
      await update.mutateAsync({ id: post.id, payload });
    }
  });

  const statusDot =
    post?.status === 'PUBLISHED'
      ? 'bg-emerald-500'
      : post?.status === 'DRAFT'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText = post?.status ? (WEBSITE_PUBLISH_STATUS_LABEL[post.status] ?? '') : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 max-sm:h-auto max-sm:flex-wrap max-sm:gap-x-2 max-sm:gap-y-1.5 max-sm:px-4 max-sm:py-2 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to blogs"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2 max-sm:max-w-[calc(100%-2.5rem)]">
          <span className="text-xs font-medium text-zinc-500 max-sm:shrink-0 max-sm:whitespace-nowrap">
            Blogs
          </span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add blog' : post ? post.title : 'Edit'}
          </span>
        </div>
        <span className="flex-1 max-sm:basis-full" />
        {!isCreate && post && statusText && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton
          variant="ghost"
          size="sm"
          disabled={!slug.trim()}
          onClick={() => window.open(blogPublicUrl(slug.trim()), '_blank', 'noopener,noreferrer')}
        >
          <ExternalLink className="mr-1.5 h-4 w-4" />
          Preview
        </UzButton>
        {/* Opt-in review: publishing stays available, this is the "let the
            manager sign off first" alternative. */}
        {!isCreate && post && post.status !== 'PENDING_REVIEW' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => submitForReview.mutate({ type: 'BLOG', id: post.id })}
            loading={submitForReview.isPending}
            disabled={pending}
          >
            Submit for review
          </UzButton>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending} disabled={!canSubmit}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && detailQuery.isLoading && <UzAlert tone="info">Loading blog post...</UzAlert>}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">Failed to load this blog post. Go back and retry.</UzAlert>
        )}

        {(isCreate || post) && (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 max-sm:grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">
              {/* Main column - the writing surface */}
              <div className="min-w-0 space-y-6">
                <Card title="Post details" description="The headline and public URL.">
                  <UzFormField name="title">
                    {({ field, error }) => (
                      <UzInput
                        {...field}
                        value={(field.value as string | undefined) ?? ''}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!slugEdited.current) form.setValue('slug', slugify(e.target.value));
                        }}
                        label="Title"
                        placeholder="e.g. Places to Visit in Kashmir"
                        error={error}
                        required
                        disabled={pending}
                      />
                    )}
                  </UzFormField>

                  <UzFormField name="slug">
                    {({ field, error }) => (
                      <div>
                        <UzInput
                          {...field}
                          value={(field.value as string | undefined) ?? ''}
                          onChange={(e) => {
                            // Custom slug: mark as edited (so the title stops
                            // overwriting it) and normalise gently while typing -
                            // keep trailing dashes so multi-word slugs are typeable.
                            slugEdited.current = true;
                            field.onChange(softSlug(e.target.value));
                          }}
                          onBlur={(e) => {
                            // Tidy leading/trailing/repeated dashes when leaving.
                            field.onChange(slugify(e.target.value));
                            field.onBlur();
                          }}
                          label="Slug"
                          placeholder="places-to-visit-in-kashmir"
                          error={error}
                          required
                          disabled={pending}
                        />
                        <p className="mt-1.5 text-[12px] break-all text-zinc-400">
                          {SITE_URL}/blog/{slug || '…'}
                        </p>
                      </div>
                    )}
                  </UzFormField>
                </Card>

                <Card title="Content" description="The article body.">
                  <UzFormField name="bodyHtml">
                    {({ field }) => (
                      <RichTextEditor
                        value={(field.value as string | undefined) ?? ''}
                        onChange={(html) => field.onChange(html)}
                        placeholder="Write the article. Use the toolbar for headings, lists, images, tables and more."
                      />
                    )}
                  </UzFormField>
                </Card>

                <Card title="FAQ" description="Frequently asked questions shown on the post.">
                  <UzFormField name="faqHtml">
                    {({ field }) => (
                      <FaqBuilder
                        value={(field.value as string | undefined) ?? ''}
                        onChange={(html) => field.onChange(html)}
                      />
                    )}
                  </UzFormField>
                </Card>
              </div>

              {/* Settings sidebar */}
              <aside className="space-y-6">
                <Card
                  title="Editor's Picks"
                  description="Feature this post in the website's Editor's Picks section."
                >
                  <UzFormField name="isFeatured">
                    {({ field }) => (
                      <UzSwitch
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) => field.onChange(checked)}
                        label="Show in Editor's Picks"
                        description="Newly featured posts appear first; the newest fills the large featured slot."
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                </Card>

                <Card
                  title="Featured image"
                  description="The main image shown on the post and in listings."
                >
                  <UzFormField name="coverMediaId">
                    {({ field }) => (
                      <MediaDropField
                        value={field.value as string | null | undefined}
                        onChange={(mediaId) => field.onChange(mediaId)}
                        kind="image"
                        folder="blog"
                      />
                    )}
                  </UzFormField>
                </Card>

                <Card
                  title="Organise"
                  description="Category, destination, theme and listing summary."
                >
                  <UzFormField name="categoryId">
                    {({ field, error }) => (
                      <UzSelect
                        name={field.name}
                        label="Category"
                        options={categoryOptions}
                        value={(field.value as string | undefined) ?? ''}
                        onValueChange={(val) => field.onChange(val)}
                        placeholder={categoriesQuery.isLoading ? 'Loading…' : 'No category'}
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                  <UzFormField name="destinationId">
                    {({ field, error }) => (
                      <UzCombobox
                        label="Destination"
                        options={destinationOptions}
                        value={(field.value as string | undefined) ?? ''}
                        onValueChange={(val) => field.onChange(val)}
                        placeholder={
                          destinationsQuery.isLoading ? 'Loading…' : 'Search a destination'
                        }
                        error={error}
                      />
                    )}
                  </UzFormField>
                  <UzFormField name="themeId">
                    {({ field, error }) => (
                      <UzCombobox
                        label="Theme"
                        options={themeOptions}
                        value={(field.value as string | undefined) ?? ''}
                        onValueChange={(val) => field.onChange(val)}
                        placeholder={themesQuery.isLoading ? 'Loading…' : 'Search a theme'}
                        error={error}
                      />
                    )}
                  </UzFormField>
                  <UzFormField name="excerpt">
                    {({ field, error }) => (
                      <UzTextarea
                        {...field}
                        value={(field.value as string | undefined) ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Excerpt"
                        placeholder="Short summary shown in listings."
                        rows={3}
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                </Card>

                <Card title="SEO" description="How the post appears in search results.">
                  <UzFormField name="metaTitle">
                    {({ field, error }) => (
                      <UzInput
                        {...field}
                        value={(field.value as string | undefined) ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Meta title"
                        hint="Defaults to the post title if blank."
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                  <UzFormField name="metaDescription">
                    {({ field, error }) => (
                      <UzTextarea
                        {...field}
                        value={(field.value as string | undefined) ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Meta description"
                        placeholder="~155 characters for search engines."
                        rows={3}
                        error={error}
                        disabled={pending}
                      />
                    )}
                  </UzFormField>
                </Card>
              </aside>
            </div>
          </UzForm>
        )}
      </div>
    </div>
  );
};
